import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';

const StudentNavigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [notificationCount] = useState(3);
  const [isOnline] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, userProfile } = useAuth();
  const profileMenuRef = useRef(null);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/student-dashboard',
      icon: 'LayoutDashboard'
    },
    {
      label: 'Exhibition Hall',
      path: '/exhibition-hall',
      icon: 'Building'
    },
    {
      label: 'Explore Institutes',
      path: '/conversations/new',
      icon: 'Building2'
    },
    {
      label: 'Auditorium',
      path: '/auditorium-webinar-hub',
      icon: 'Video'
    },
    {
      label: 'My Conversations',
      path: '/chat-interface',
      icon: 'MessageSquare',
      badge: notificationCount
    },
    {
      label: 'Video Counseling',
      path: '/video-counseling',
      icon: 'Video'
    },
    {
      label: 'Group Sessions',
      path: '/group-counseling-sessions',
      icon: 'Users'
    }
  ];

  const isActivePath = (path) => location?.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/student-registration');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef?.current && !profileMenuRef?.current?.contains(event?.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 h-[60px] bg-card shadow-md z-1000">
        <div className="max-w-[1440px] mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/student-dashboard" className="flex items-center gap-3 transition-smooth hover-lift">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="GraduationCap" size={24} color="var(--color-primary)" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">Studyexpo</span>
            </Link>

            <div className="hidden lg:flex items-center gap-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`relative px-4 py-2 rounded-lg transition-smooth hover-lift ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={item?.icon} 
                      size={20} 
                      color={isActivePath(item?.path) ? 'var(--color-primary-foreground)' : 'currentColor'} 
                    />
                    <span className="font-medium">{item?.label}</span>
                    {item?.badge && item?.badge > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs font-semibold rounded-full flex items-center justify-center">
                        {item?.badge}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
              <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-success' : 'bg-muted-foreground'}`} />
              <span className="text-sm font-medium text-muted-foreground">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => {}}
            >
              <Icon name="Bell" size={20} />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs font-semibold rounded-full flex items-center justify-center">
                  {notificationCount}
                </span>
              )}
            </Button>

            <div className="relative" ref={profileMenuRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              >
                <Icon name="User" size={20} />
              </Button>
              {isProfileMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-sm font-medium text-foreground">{userProfile?.full_name || 'Student'}</p>
                    <p className="text-xs text-muted-foreground truncate">{userProfile?.email || ''}</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                  >
                    <Icon name="LogOut" size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </nav>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-[60px] bg-background z-1020 lg:hidden">
          <div className="flex flex-col p-6 gap-2">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`relative flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon 
                  name={item?.icon} 
                  size={20} 
                  color={isActivePath(item?.path) ? 'var(--color-primary-foreground)' : 'currentColor'} 
                />
                <span className="font-medium">{item?.label}</span>
                {item?.badge && item?.badge > 0 && (
                  <span className="ml-auto w-6 h-6 bg-accent text-accent-foreground text-xs font-semibold rounded-full flex items-center justify-center">
                    {item?.badge}
                  </span>
                )}
              </Link>
            ))}

            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center justify-between px-4 py-3 bg-muted rounded-lg mb-3">
                <span className="text-sm font-medium text-muted-foreground">Status</span>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-success' : 'bg-muted-foreground'}`} />
                  <span className="text-sm font-medium">
                    {isOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>

              <Button
                variant="outline"
                fullWidth
                iconName="Bell"
                iconPosition="left"
                className="mb-3"
                onClick={() => {}}
              >
                Notifications
                {notificationCount > 0 && (
                  <span className="ml-auto w-6 h-6 bg-accent text-accent-foreground text-xs font-semibold rounded-full flex items-center justify-center">
                    {notificationCount}
                  </span>
                )}
              </Button>

              <Button
                variant="outline"
                fullWidth
                iconName="LogOut"
                iconPosition="left"
                onClick={handleSignOut}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentNavigation;